import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCurrentShift, openShift, closeShift,
  getBranches, getProducts, getCategories, getCustomers, getCurrencies,
  createOrder, getOrders, getOrderById, cancelOrder,
  getVoucherByCode, sellVoucher,
  getServices,
  getSections, getTables,
  getPaymentGatewayStatus, getOrderPaymentStatus, retryOrderPayment, cancelPendingPayment,
  getAssetUrl,
} from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import SearchInput from "../components/UI/SearchInput";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { aggregateTaxBreakdown, taxRateLabel } from "../utils/taxBreakdown";
import { useTranslation } from "../context/I18nContext";
import { renderTemplate } from "../utils/i18nTemplate";

// A fresh payment row — always includes the voucher-only fields (even
// though they're unused by every other method) so a row never flips from
// uncontrolled to controlled inputs the moment its method changes to
// "voucher".
const emptyPaymentRow = () => ({
  method: "cash", amount: "", received_amount: "",
  voucher_code: "", voucher_balance: null, voucher_currency_id: null, voucher_error: "", forfeit_remainder: false,
});

// SellVoucherModal is a standalone action, deliberately independent of
// the cart/customer state around it — selling a gift voucher isn't a
// product sale (no tax, no stock, no cart line), so it gets its own
// customer picker instead of assuming the sale's main "Customer
// (optional)" selection is the same person the voucher is for.
function SellVoucherModal({ customerOptions, onClose, onSold }) {
  const token = localStorage.getItem("token");
  const { t } = useTranslation("checkout");
  const { t: tCommon } = useTranslation("common");

  // Paying FOR a new voucher can't itself be "voucher" — see
  // SellVoucherHandler's own validVoucherSalePaymentMethods.
  const SELL_VOUCHER_PAYMENT_OPTIONS = [
    { value: "cash", label: t("payment_method_cash", "Cash") },
    { value: "card", label: t("payment_method_card", "Card") },
    { value: "wallet", label: t("payment_method_wallet", "Wallet") },
    { value: "other", label: t("payment_method_other", "Other") },
  ];

  const [amount, setAmount] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    if (!amount || Number(amount) <= 0) {
      notify.error(t("validation_amount_required", "Enter an amount greater than zero"));
      return;
    }
    if (method === "wallet" && !customerId) {
      notify.error(t("validation_wallet_customer_required", "A customer must be selected to pay from their wallet"));
      return;
    }
    setSubmitting(true);
    try {
      const voucher = await sellVoucher(token, {
        amount: Number(amount),
        customer_id: customerId ? Number(customerId) : 0,
        payment_method: method,
        reference: reference.trim(),
      });
      setResult(voucher);
      onSold();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_sell_voucher_failed", "Failed to sell voucher"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="relative bg-white rounded-[2.5rem] p-8 max-w-md w-full shadow-2xl">
        {result ? (
          <div className="text-center">
            <ICONS.Vouchers className="w-14 h-14 mx-auto text-emerald-500 mb-3" />
            <h3 className="text-xl font-black text-gray-900 mb-1">{t("voucher_sold_title", "Voucher Sold")}</h3>
            <p className="text-gray-400 text-sm mb-4">{t("voucher_sold_subtitle", "Give this code to the customer")}</p>
            <div className="bg-gray-50/60 rounded-2xl p-4 mb-6">
              <p className="font-mono text-2xl font-black text-gray-800">{result.code}</p>
              <p className="text-sm text-gray-500 mt-1">{money(result.balance)} {result.currency?.symbol || result.currency?.code || ""}</p>
            </div>
            <Button variant="primary" onClick={onClose} className="w-full">{t("btn_done", "Done")}</Button>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-black text-gray-900 mb-1">{t("sell_voucher_modal_title", "Sell a Voucher")}</h3>
            <p className="text-gray-400 text-sm mb-6">{t("sell_voucher_modal_subtitle", "Issues a brand-new voucher for real money — separate from a product sale")}</p>

            <div className="space-y-5">
              <FloatingInput label={t("field_amount_label", "Amount")} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} icon={ICONS.Vouchers} />
              <SelectBox label={t("field_customer_optional_label", "Customer (optional)")} options={customerOptions} value={customerId} onChange={setCustomerId} />
              <SelectBox label={t("field_payment_method_label", "Payment Method")} options={SELL_VOUCHER_PAYMENT_OPTIONS} value={method} onChange={setMethod} />
              <FloatingInput label={t("field_reference_label", "Reference (optional)")} value={reference} onChange={(e) => setReference(e.target.value)} />

              <div className="flex gap-3 pt-2">
                <Button variant="primary" onClick={handleSubmit} loading={submitting} className="flex-1">{t("btn_sell_voucher", "Sell Voucher")}</Button>
                <Button variant="secondary" onClick={onClose} disabled={submitting} className="flex-1">{tCommon("btn_cancel", "Cancel")}</Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function money(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Mirrors ServiceCapableBusinessTypes in internal/handlers/branch_handler.go
// exactly — advisory only there, and the same here: this is purely what
// decides whether the "Services" tab even shows up in this screen, checked
// against the open shift's own branch (the one place a "current branch"
// concept genuinely exists in this app). The backend never enforces this
// itself (see CreateOrderHandler's service-item pass in order_handler.go) —
// a branch that's since been switched to a non-service type still HONORS a
// service sale already in flight, it just stops OFFERING new ones here.
const SERVICE_CAPABLE_BUSINESS_TYPES = { salon: true, beauty_center: true };

// Mirrors TableCapableBusinessTypes in internal/handlers/branch_handler.go —
// the same "advisory client-side gate" convention as
// SERVICE_CAPABLE_BUSINESS_TYPES above: this only decides whether the
// "Dine-in" order type (and its Table picker) is even offered here, checked
// against the open shift's own branch. The backend doesn't hard-block a
// dine_in order for any other business type either — see CreateOrderHandler
// in order_handler.go — it just isn't something this screen offers for a
// branch that never set up Sections/Tables.
const TABLE_CAPABLE_BUSINESS_TYPES = { restaurant: true, hybrid: true };

// Mirrors internal/services/tax_calc.go exactly, so the cart's live total
// matches what CreateOrderHandler actually charges at checkout — see
// docs/order-design.md section 2. Reads the same preloaded shape
// ListProductsHandler now sends (TaxGroup.Items.Tax.GeoZone.Countries/Cities).
function geoZoneAppliesToBranch(geoZone, branch) {
  if (!geoZone) return true;
  if (branch?.city_id && (geoZone.cities || []).some((c) => c.id === branch.city_id)) return true;
  if (branch?.country_id && (geoZone.countries || []).some((c) => c.id === branch.country_id)) return true;
  return false;
}

function calculateLineTax(taxableBase, quantity, exchangeRate, group, branch) {
  if (!group || !group.is_active) return { addOn: 0, breakdown: [] };
  let addOnTotal = 0;
  let runningExclusive = 0;
  const breakdown = [];
  const items = [...(group.items || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  for (const gi of items) {
    const tax = gi.tax;
    if (!tax || !tax.is_active) continue;
    if (!geoZoneAppliesToBranch(tax.geo_zone, branch)) continue;

    let base = taxableBase;
    if (tax.is_compound) base += runningExclusive;

    const amount = tax.calculation_method === "fixed_amount"
      ? tax.rate * exchangeRate * quantity
      : (base * tax.rate) / 100;

    breakdown.push({ tax_id: tax.id, tax_name: tax.name, rate: tax.rate, amount, calculation_method: tax.calculation_method, is_price_inclusive: tax.is_price_inclusive });
    if (!tax.is_price_inclusive) {
      addOnTotal += amount;
      runningExclusive += amount;
    }
  }
  return { addOn: addOnTotal, breakdown };
}

// Mirrors services.EffectiveDiscount (internal/services/discount_engine.go)
// for the two discount sources that only ever depend on the product +
// quantity being bought — the product's own single discount, then its
// Quantity Discount tiers. Sale- and BundleOffer-driven discounts are NOT
// mirrored here (see ListProductsHandler's doc comment): those need data
// this screen doesn't fetch, so they're only reflected on the receipt
// after the sale actually completes, not live in this cart preview.
function discountAmountFor(discountType, value, lineGross, quantity, exchangeRate) {
  if (discountType === "percentage") return (lineGross * value) / 100;
  if (discountType === "fixed") return value * exchangeRate * quantity;
  return 0;
}

function clampDiscount(amount, max) {
  if (amount < 0) return 0;
  if (amount > max) return max;
  return amount;
}

function isWithinDateRange(start, end, now) {
  if (start && now < new Date(start)) return false;
  if (end && now > new Date(new Date(end).getTime() + 24 * 60 * 60 * 1000 - 1)) return false;
  return true;
}

// autoLineDiscount mirrors services.EffectiveDiscount's product > quantity
// > sale priority for a live, accurate cart preview. The product and
// quantity-tier branches recompute the real rule locally (percentage or
// fixed, at THIS line's actual quantity) same as the backend. The Sale
// branch is different: a Sale's own percentage/product membership isn't
// shipped to the client at all — instead the product list/detail response
// already carries discount_source/discounted_price, resolved server-side
// by services.CurrentDisplayDiscount at quantity 1 (see Product.go and
// product_handler.go's attachDisplayDiscounts). Since a Sale is always a
// flat percentage (models.Sale has no "fixed" option), the percentage
// implied by that qty-1 snapshot is exactly the same percentage that
// applies at any quantity, so it can be reapplied here to the line's
// REAL lineGross without needing to duplicate any Sale lookup logic in
// JS. Bundle discounts are handled separately, one level up in lineCalcs
// (see applyBundleOffers below) — a bundle's reward depends on every
// other line in the cart too, which this single-line helper has no
// visibility into, so lineCalcs never even calls this function for a
// bundled line (see EffectiveDiscount's own doc comment for the same
// bundle-exclusivity rule on the backend).
function autoLineDiscount(product, baseQty, lineGross, exchangeRate) {
  const now = new Date();
  if (product.discount_type && product.discount_value > 0 && isWithinDateRange(product.discount_start_date, product.discount_end_date, now)) {
    const amt = clampDiscount(discountAmountFor(product.discount_type, product.discount_value, lineGross, baseQty, exchangeRate), lineGross);
    if (amt > 0) return amt;
  }
  const tiers = [...(product.quantity_discounts || [])].sort((a, b) => b.min_quantity - a.min_quantity);
  for (const t of tiers) {
    if (baseQty < t.min_quantity || !isWithinDateRange(t.start_date, t.end_date, now)) continue;
    const amt = clampDiscount(discountAmountFor(t.discount_type, t.discount_value, lineGross, baseQty, exchangeRate), lineGross);
    if (amt > 0) return amt;
    break;
  }
  if (product.discount_source === "sale" && product.discounted_price != null && product.price > 0) {
    const salePercent = Math.max(0, (product.price - product.discounted_price) / product.price);
    const amt = clampDiscount(lineGross * salePercent, lineGross);
    if (amt > 0) return amt;
  }
  return 0;
}

// applyBundleOffers is the exact JS mirror of services.ApplyBundleOffers
// in internal/services/discount_engine.go: "buy requiredQuantity, cheapest
// one free" across every unit passed in, POOLED TOGETHER regardless of
// which cart line each unit came from. Sort every unit by price
// descending, chunk the sorted list into consecutive groups of
// requiredQuantity, and in every COMPLETE group the last (cheapest) unit
// is free. A leftover partial group (fewer than requiredQuantity units)
// gets no discount — not enough units yet to earn the reward. Returns
// both the discount amount AND the free-unit count per line index, so
// lineCalcs can show "N free" next to the line as well as adjust its
// total.
function applyBundleOffers(units, requiredQuantity) {
  const discountByLine = {};
  const freeCountByLine = {};
  if (requiredQuantity < 2 || units.length < requiredQuantity) return { discountByLine, freeCountByLine };

  const sorted = [...units].sort((a, b) => b.unitPrice - a.unitPrice);
  for (let i = 0; i + requiredQuantity <= sorted.length; i += requiredQuantity) {
    const free = sorted[i + requiredQuantity - 1];
    discountByLine[free.lineIndex] = (discountByLine[free.lineIndex] || 0) + free.unitPrice;
    freeCountByLine[free.lineIndex] = (freeCountByLine[free.lineIndex] || 0) + 1;
  }
  return { discountByLine, freeCountByLine };
}

function OpenShiftPanel({ branches, onOpened }) {
  const token = localStorage.getItem("token");
  const { t } = useTranslation("checkout");
  const [branchId, setBranchId] = useState("");
  const [openingBalance, setOpeningBalance] = useState("0");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const branchOptions = branches.map((b) => ({ value: String(b.id), label: b.name }));

  const handleSubmit = async () => {
    const newErrors = {};
    if (!branchId) newErrors.branch_id = t("validation_branch_required", "Pick the branch you're working at");
    if (openingBalance === "" || Number(openingBalance) < 0) newErrors.opening_balance = t("validation_opening_balance_required", "Enter a valid opening balance");
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    try {
      const shift = await openShift(token, { branch_id: Number(branchId), opening_balance: Number(openingBalance) });
      notify.success(t("notify_shift_opened", "Shift opened — you're ready to sell"));
      onOpened(shift);
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_open_shift_failed", "Failed to open shift"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm max-w-md w-full">
        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 text-[var(--primary-color)] mb-6 mx-auto">
          <ICONS.Checkout className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-black text-gray-900 text-center mb-1">{t("open_shift_title", "Open a Shift to Start Selling")}</h2>
        <p className="text-gray-400 text-sm text-center mb-6">{t("open_shift_subtitle", "Every sale has to belong to an open cash register session")}</p>

        <div className="space-y-5">
          <SelectBox label={t("field_branch_label", "Branch")} options={branchOptions} value={branchId} onChange={setBranchId} error={errors.branch_id} icon={ICONS.Branches} />
          <FloatingInput
            label={t("field_opening_balance_label", "Opening Cash Balance")}
            type="number"
            value={openingBalance}
            onChange={(e) => setOpeningBalance(e.target.value)}
            error={errors.opening_balance}
          />
          <Button variant="primary" onClick={handleSubmit} loading={loading} className="w-full" icon={ICONS.Checkout}>
            {t("btn_open_shift", "Open Shift")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CloseShiftModal({ shift, onClose, onClosed }) {
  const token = localStorage.getItem("token");
  const { t } = useTranslation("checkout");
  const { t: tCommon } = useTranslation("common");
  const [closingBalance, setClosingBalance] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (closingBalance === "" || Number(closingBalance) < 0) {
      setError(t("validation_actual_cash_required", "Enter the actual counted cash amount"));
      return;
    }
    setLoading(true);
    try {
      await closeShift(token, shift.id, { closing_balance: Number(closingBalance) });
      notify.success(t("notify_shift_closed", "Shift closed"));
      onClosed();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_close_shift_failed", "Failed to close shift"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="relative bg-white rounded-[2.5rem] p-8 max-w-md w-full shadow-2xl">
        <h3 className="text-xl font-black text-gray-900 mb-1">{t("close_shift_modal_title", "Close Shift")}</h3>
        <p className="text-gray-400 text-sm mb-6">{t("close_shift_modal_subtitle", "Opened with {amount}").replace("{amount}", money(shift.opening_balance))}</p>
        <div className="space-y-5">
          <FloatingInput
            label={t("field_actual_cash_label", "Actual Cash Counted")}
            type="number"
            value={closingBalance}
            onChange={(e) => { setClosingBalance(e.target.value); setError(""); }}
            error={error}
          />
          <div className="flex gap-3 pt-2">
            <Button variant="primary" onClick={handleSubmit} loading={loading} className="flex-1">{t("btn_close_shift", "Close Shift")}</Button>
            <Button variant="secondary" onClick={onClose} disabled={loading} className="flex-1">{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Lists held orders (see OrderStatusHeld in models/order.go) parked at this
// branch, filtered server-side to just this cashier when the branch's
// RestrictHeldOrdersToCashier setting is on (see AddEditBranch.js) — the
// caller decides that scoping and just hands this the already-fetched list.
// Cancelling (see CancelHeldOrderHandler) always requires a reason, which
// is what the inline text box below is for — the button only actually
// fires onCancel once one's been typed.
function HeldOrdersModal({ orders, loading, onClose, onResume, onCancel, cancellingId }) {
  const { t } = useTranslation("common");
  const { t: tCheckout } = useTranslation("checkout");
  const [cancelingRowId, setCancelingRowId] = useState(null);
  const [reason, setReason] = useState("");

  const startCancel = (id) => {
    setCancelingRowId(id);
    setReason("");
  };

  const submitCancel = (order) => {
    if (!reason.trim()) {
      notify.error(tCheckout("validation_cancel_reason_required", "A cancellation reason is required"));
      return;
    }
    onCancel(order, reason.trim());
    setCancelingRowId(null);
    setReason("");
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="relative bg-white rounded-[2.5rem] p-8 max-w-lg w-full shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xl font-black text-gray-900">{t("btn_held_orders", "Held Orders")}</h3>
          <button onClick={onClose} className="text-gray-300 hover:text-red-500">
            <ICONS.Close className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-400 text-sm mb-6">{t("held_orders_subtitle", "Parked carts waiting to be picked back up")}</p>

        <div className="flex-1 overflow-y-auto space-y-2">
          {loading && <p className="text-center text-gray-400 italic py-8">{t("loading_text", "Loading…")}</p>}
          {!loading && orders.length === 0 && (
            <p className="text-center text-gray-400 italic py-8">{t("held_orders_empty", "No held orders right now")}</p>
          )}
          {!loading && orders.map((o) => (
            <div key={o.id} className="border border-gray-100 rounded-2xl p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-sm text-gray-800">{t("held_order_number_prefix", "Order #")}{o.id}</div>
                  <div className="text-[11px] text-gray-400">
                    {renderTemplate(t("held_order_meta_line", "{customer} · held by {cashier} · {time}"), {
                      customer: o.customer?.name || t("held_order_walkin", "Walk-in"),
                      cashier: o.cashier?.username || `#${o.cashier_id}`,
                      time: new Date(o.created_at).toLocaleTimeString(),
                    })}
                  </div>
                  <div className="text-[11px] font-mono text-gray-500 mt-0.5">{money(o.grand_total)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => (cancelingRowId === o.id ? setCancelingRowId(null) : startCancel(o.id))}
                    className="text-gray-300 hover:text-red-500 p-2"
                    title={t("cancel_held_order_title", "Cancel this held order")}
                  >
                    <ICONS.Close className="w-5 h-5" />
                  </button>
                  <Button variant="primary" onClick={() => onResume(o)} icon={ICONS.Checkout}>{t("btn_resume", "Resume")}</Button>
                </div>
              </div>
              {cancelingRowId === o.id && (
                <div className="mt-3 pt-3 border-t border-gray-50 space-y-2">
                  <FloatingInput
                    label={t("reason_for_cancelling_label", "Reason for cancelling")}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="danger"
                      onClick={() => submitCancel(o)}
                      loading={cancellingId === o.id}
                      className="flex-1"
                    >
                      {t("btn_confirm_cancel", "Confirm Cancel")}
                    </Button>
                    <Button variant="secondary" onClick={() => setCancelingRowId(null)} className="flex-1">
                      {t("btn_never_mind", "Never Mind")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Checkout() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("common");
  const { t: tCheckout } = useTranslation("checkout");

  const PAYMENT_METHOD_OPTIONS = [
    { value: "cash", label: tCheckout("payment_method_cash", "Cash") },
    { value: "card", label: tCheckout("payment_method_card", "Card") },
    { value: "wallet", label: tCheckout("payment_method_wallet", "Wallet") },
    { value: "voucher", label: tCheckout("payment_method_voucher", "Voucher") },
    { value: "other", label: tCheckout("payment_method_other", "Other") },
  ];

  const [loadingShift, setLoadingShift] = useState(true);
  const [shift, setShift] = useState(null);
  const [showCloseShift, setShowCloseShift] = useState(false);

  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [services, setServices] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  // "products" or "services" — which grid the picker on the left shows.
  // Only ever "services" when canSellServices is true (see below); a
  // branch that isn't service-capable never even sees the tab.
  const [pickerTab, setPickerTab] = useState("products");
  // unit_id is "" for the product's own base unit, or the string id of one
  // of its product_units — quantity is always expressed in THAT unit, and
  // gets converted to the base unit for pricing/tax/stock exactly like
  // CreateOrderHandler does server-side (see resolveLineUnit in
  // order_handler.go and lineCalcs below).
  const [cart, setCart] = useState([]); // [{product, quantity, unit_id, discount_amount, notes}]
  // serviceCart is a SEPARATE list from cart (products) — a service line
  // never has a quantity/unit (always implicit qty 1, see
  // checkoutServiceItemPayload's own doc comment in order_handler.go) and
  // instead carries staffIds: who performed it, chosen AFTER adding the
  // line (mirrors how a product line's unit is chosen after adding it).
  // `id` is a stable per-line identity (see nextServiceLineId below) since,
  // unlike cart, adding the same service twice never merges into one line
  // — each sale of a service needs its own staff assignment.
  const [serviceCart, setServiceCart] = useState([]); // [{id, service, staffIds, discount_amount, notes}]
  const nextServiceLineId = useRef(0);
  const [customerId, setCustomerId] = useState("");
  // orderType/tableId are new (see models.Order.TableID/KitchenStatus in
  // order.go): tableId is only ever sent to the backend when orderType is
  // "dine_in" (see submitCheckout/handleHoldSale below) — picking a table
  // then switching back to another order type just leaves the selection in
  // place, harmlessly ignored, rather than resetting it, so flipping back to
  // Dine-in remembers the cashier's last pick.
  const [orderType, setOrderType] = useState("retail_sale");
  const [tableId, setTableId] = useState("");
  const [sections, setSections] = useState([]);
  const [tables, setTables] = useState([]);
  const [payments, setPayments] = useState([emptyPaymentRow()]);
  // checkingVoucherIdx tracks which payment row's "Check" button is
  // currently in flight, so only that row shows a loading state.
  const [checkingVoucherIdx, setCheckingVoucherIdx] = useState(-1);
  // voucherForfeitPrompt is set when the backend rejects a checkout with
  // "voucher_full_redemption_required" — see submitCheckout below. Holds
  // exactly what the confirmed design calls for: the customer/cashier is
  // shown the voucher's balance and the shortfall, and chooses between
  // forfeiting the remainder (resubmits with forfeit_remainder: true on
  // that row) or backing out to add more items first.
  const [voucherForfeitPrompt, setVoucherForfeitPrompt] = useState(null);
  // Sell-a-voucher is a standalone action, unrelated to the current
  // cart/payments — see SellVoucherModal.
  const [showSellVoucher, setShowSellVoucher] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [holding, setHolding] = useState(false);

  // pendingPayment is set the moment a checkout comes back
  // status:"awaiting_payment_confirmation" (a real payment gateway is
  // active for this currency — see services.ActiveGateway/CreateOrderHandler)
  // — { order, transaction }. While it's set, the screen shows the waiting
  // view below instead of the normal cart, and a poll (see the useEffect
  // near submitCheckout) is what actually confirms or fails the sale —
  // GetOrderPaymentStatusHandler is a cheap DB-only read, never the gateway
  // itself. Cancelling clears only this — the cart/payments state is
  // untouched so the cashier can pick a different method (see
  // handleCancelPendingPayment below), unlike a genuinely completed sale
  // which calls the full resetSale().
  const [pendingPayment, setPendingPayment] = useState(null);
  const [payingAction, setPayingAction] = useState(false);
  // gatewayActive mirrors GetPaymentGatewayStatusHandler's own "active"
  // flag for the branch's currency — purely informational today (a small
  // hint on the Card option), since the backend alone decides, per order,
  // whether the gateway path actually applies. A deployment that never
  // configures a gateway just gets `false` here and nothing else changes.
  const [gatewayActive, setGatewayActive] = useState(false);

  // resumingOrderId is set once a held order (see OrderStatusHeld,
  // models/order.go) has been loaded back into the cart below — it's what
  // tells handleCompleteSale to send resume_order_id so the backend updates
  // that same Order.ID in place instead of creating a new one, keeping the
  // receipt number the cashier was quoted when they first held it.
  const [resumingOrderId, setResumingOrderId] = useState(null);
  const [showHeldOrders, setShowHeldOrders] = useState(false);
  const [heldOrders, setHeldOrders] = useState([]);
  const [loadingHeld, setLoadingHeld] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);

  // Every tap on a product tile (see addToCart) scrolls that exact line
  // into view — whether it just landed as a brand-new line at the end of
  // the cart, or it was already sitting further up and only its quantity
  // just bumped. lineRefs keys match each rendered line's own React key
  // (`${product.id}-${unit_id || "base"}`), and scrollTarget carries a
  // fresh timestamp on every tap (not just every new key) so tapping the
  // same product twice in a row re-triggers the scroll both times, even
  // though the key didn't change.
  const lineRefs = useRef({});
  const [scrollTarget, setScrollTarget] = useState(null); // { key, ts }
  useEffect(() => {
    if (!scrollTarget) return;
    lineRefs.current[scrollTarget.key]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [scrollTarget]);

  const loadShift = async () => {
    setLoadingShift(true);
    try {
      const s = await getCurrentShift(token);
      setShift(s);
    } catch (err) {
      setShift(null);
    } finally {
      setLoadingShift(false);
    }
  };

  useEffect(() => { loadShift(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getBranches(token).then(setBranches).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shift) return;
    getProducts(token).then(setProducts).catch(() => notify.error(tCheckout("notify_load_products_failed", "Failed to load products")));
    getCategories(token).then(setCategories).catch(() => {});
    getCustomers(token).then(setCustomers).catch(() => {});
    getCurrencies(token).then(setCurrencies).catch(() => {});
    // Fetched unconditionally (not gated on canSellServices below) — cheap
    // either way, and it means a branch resuming an OLD held order that
    // happens to carry service lines (e.g. after a business_type change)
    // can still look those services up by id in handleResumeOrder.
    getServices(token).then(setServices).catch(() => notify.error(tCheckout("notify_load_services_failed", "Failed to load services")));
    // Fetched unconditionally too, same reasoning as getServices above — a
    // held order resumed after a business_type change can still carry a
    // table_id that needs a name to display.
    getSections(token).then(setSections).catch(() => {});
    getTables(token).then(setTables).catch(() => {});
  }, [shift, token]);

  const branch = shift?.branch;
  // See SERVICE_CAPABLE_BUSINESS_TYPES's own doc comment above — this is
  // what actually shows/hides the "Services" tab in the picker below.
  const canSellServices = !!SERVICE_CAPABLE_BUSINESS_TYPES[branch?.business_type];
  // See TABLE_CAPABLE_BUSINESS_TYPES's own doc comment above — this is what
  // shows/hides the "Dine-in" order type option and its Table picker.
  const canUseTables = !!TABLE_CAPABLE_BUSINESS_TYPES[branch?.business_type];
  // Tables/getTables is fetched unfiltered above (see the reasoning at
  // getSections/getTables) — narrowed to THIS branch here, the same
  // client-side pattern Tables.js itself uses for its own branch filter.
  const branchTables = useMemo(
    () => tables.filter((tbl) => branch && Number(tbl.branch_id) === branch.id),
    [tables, branch]
  );
  const tableOptions = [
    { value: "", label: tCheckout("select_table_placeholder", "Select a table") },
    ...branchTables.map((tbl) => {
      const section = sections.find((s) => s.id === tbl.section_id);
      return { value: String(tbl.id), label: section ? `${tbl.name} (${section.name})` : tbl.name };
    }),
  ];
  const ORDER_TYPE_OPTIONS = [
    { value: "retail_sale", label: tCheckout("order_type_option_retail_sale", "Retail Sale") },
    ...(canUseTables ? [{ value: "dine_in", label: tCheckout("order_type_option_dine_in", "Dine-in") }] : []),
    { value: "takeaway", label: tCheckout("order_type_option_takeaway", "Takeaway") },
    { value: "delivery", label: tCheckout("order_type_option_delivery", "Delivery") },
  ];
  const activeServices = useMemo(() => services.filter((s) => s.is_active), [services]);
  const effectiveCurrency = useMemo(() => {
    if (!branch || currencies.length === 0) return null;
    if (branch.currency_id) return currencies.find((c) => c.id === branch.currency_id) || null;
    return currencies.find((c) => c.is_base) || null;
  }, [branch, currencies]);
  const exchangeRate = effectiveCurrency?.exchange_rate || 1;
  const currencyLabel = effectiveCurrency?.symbol || effectiveCurrency?.code || "";

  // Purely informational (see gatewayActive's own doc comment above) — has
  // no effect on what actually gets sent to createOrder.
  useEffect(() => {
    if (!effectiveCurrency?.code) return;
    getPaymentGatewayStatus(token, effectiveCurrency.code)
      .then((res) => setGatewayActive(!!res?.active))
      .catch(() => setGatewayActive(false));
  }, [token, effectiveCurrency?.code]);

  // A page refresh (or a crash/close) while a hosted payment page was open
  // in the other tab would otherwise strand that order — still reserved,
  // still awaiting_payment_confirmation server-side, but invisible here.
  // ListOrdersHandler already supports this exact filter with zero backend
  // change (see the plan) — finding one resumes straight into the waiting
  // screen below, never back into the editable cart (a gateway-pending
  // order is locked, same as CancelHeldOrderHandler's resume path only ever
  // accepting Held).
  useEffect(() => {
    if (!shift?.cashier_id) return;
    getOrders(token, { status: "awaiting_payment_confirmation", cashier_id: shift.cashier_id })
      .then((orders) => {
        const stranded = (orders || [])[0];
        if (!stranded) return;
        const tx = (stranded.payment_transactions || [])[0];
        setPendingPayment({ order: stranded, transaction: tx || null });
      })
      .catch(() => {});
  }, [shift?.cashier_id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (!p.is_active) return false;
      if (categoryFilter && String(p.category_id) !== categoryFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return p.name.toLowerCase().includes(term) || p.sku?.toLowerCase().includes(term) || p.barcode?.toLowerCase().includes(term);
      }
      return true;
    });
  }, [products, categoryFilter, searchTerm]);

  // Services now share the SAME Category table Products do (see
  // models.Category's doc comment), so the same category chip bar/
  // categoryFilter state below filters both grids — a service line is
  // just filtered by its own category_id instead of a product's.
  const filteredServices = useMemo(() => {
    return activeServices.filter((s) => {
      if (categoryFilter && String(s.category_id) !== categoryFilter) return false;
      if (searchTerm) {
        return s.name.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return true;
    });
  }, [activeServices, categoryFilter, searchTerm]);

  // The product picker is paged 10-at-a-time (2 rows of 5 on sm+ screens)
  // instead of scrolling vertically — each page is a full-width snap point
  // in a horizontally-scrolling strip (see productPagerRef below), so
  // dragging/swiping left reveals the next page and dragging right goes
  // back, all the way to the first. PAGE_SIZE=10 is a plain constant (not
  // configurable) since "2 rows of 5" is the actual spec, not just a
  // default — must stay in sync with the grid-cols-5 on the page div below.
  const PRODUCT_PAGE_SIZE = 10;
  const productPages = useMemo(() => {
    const pages = [];
    for (let i = 0; i < filteredProducts.length; i += PRODUCT_PAGE_SIZE) {
      pages.push(filteredProducts.slice(i, i + PRODUCT_PAGE_SIZE));
    }
    return pages.length > 0 ? pages : [[]];
  }, [filteredProducts]);

  const productPagerRef = useRef(null);
  const [currentProductPage, setCurrentProductPage] = useState(0);

  // Whenever the search term or category filter changes, the set of pages
  // is effectively brand new — snap straight back to page 1 rather than
  // leaving the strip scrolled to whatever position it was at for the
  // PREVIOUS filter's page count (which might not even exist anymore).
  useEffect(() => {
    setCurrentProductPage(0);
    productPagerRef.current?.scrollTo({ left: 0, behavior: "auto" });
  }, [searchTerm, categoryFilter]);

  const handleProductPagerScroll = () => {
    const el = productPagerRef.current;
    if (!el || el.clientWidth === 0) return;
    setCurrentProductPage(Math.round(el.scrollLeft / el.clientWidth));
  };

  const customerOptions = [{ value: "", label: tCheckout("customer_walkin_option", "Walk-in (no customer)") }, ...customers.filter((c) => c.is_active).map((c) => ({ value: String(c.id), label: c.name }))];

  const addToCart = (product) => {
    setCart((prev) => {
      // Only merges into an existing BASE-unit line for this product — a
      // line already switched to an alternate unit stays its own line, so
      // changing units never silently merges two different quantities.
      const idx = prev.findIndex((l) => l.product.id === product.id && !l.unit_id);
      if (idx >= 0) {
        const next = [...prev];
        const step = product.allows_fractional_qty ? 0.5 : 1;
        next[idx] = { ...next[idx], quantity: +(next[idx].quantity + step).toFixed(2) };
        return next;
      }
      return [...prev, { product, quantity: 1, unit_id: "", discount_amount: 0, notes: "" }];
    });
    // addToCart only ever touches a line with no unit_id (see the findIndex
    // above), so "base" is always the right half of that line's own key.
    setScrollTarget({ key: `${product.id}-base`, ts: Date.now() });
  };

  // Real barcode-scanner support. A keyboard-wedge scanner is, to the
  // browser, indistinguishable from someone typing very fast and hitting
  // Enter — there is no separate "scan" event. So this listens globally
  // (window, capture phase) rather than on the search box, meaning a scan
  // works no matter what's focused, including nothing at all — the
  // cashier never has to click into the search box first. What tells a
  // scan apart from ordinary typing is speed: a burst of characters is
  // only treated as a barcode if every gap between keystrokes, right up
  // to the final Enter, stayed under SCAN_MAX_GAP_MS. Sustained human
  // typing essentially never gets that fast, so a manually-typed Enter
  // (e.g. from actually typing into the product search box) always falls
  // through untouched and behaves exactly as before.
  //
  // A match reuses addToCart as-is — a second scan of something already
  // in the cart bumps its quantity instead of adding a duplicate line,
  // for free, the same as tapping its tile twice.
  const scanBufferRef = useRef({ chars: "", lastAt: 0 });
  useEffect(() => {
    const SCAN_MAX_GAP_MS = 30;
    const SCAN_MIN_LENGTH = 3;

    const handleScanKeyDown = (e) => {
      if (submitting) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const buf = scanBufferRef.current;
      const now = Date.now();

      if (e.key === "Enter") {
        const gap = now - buf.lastAt;
        const chars = buf.chars;
        buf.chars = "";
        buf.lastAt = 0;
        if (chars.length < SCAN_MIN_LENGTH || gap > SCAN_MAX_GAP_MS) return;

        e.preventDefault();
        const match = products.find(
          (p) => p.is_active && p.barcode && p.barcode.toLowerCase() === chars.toLowerCase()
        );
        if (match) {
          addToCart(match);
          setSearchTerm("");
        } else {
          notify.error(tCheckout("notify_barcode_not_found", "No product found for barcode \"{barcode}\"").replace("{barcode}", chars));
        }
        return;
      }

      // Only single printable characters accumulate into the buffer —
      // Tab/Shift/arrow keys/etc. neither belong in a barcode nor should
      // reset a burst that's still within its timing window.
      if (e.key.length !== 1) return;

      if (now - buf.lastAt > SCAN_MAX_GAP_MS) buf.chars = "";
      buf.chars += e.key;
      buf.lastAt = now;
    };

    window.addEventListener("keydown", handleScanKeyDown, true);
    return () => window.removeEventListener("keydown", handleScanKeyDown, true);
  }, [products, submitting]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateLine = (idx, patch) => {
    setCart((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const removeLine = (idx) => setCart((prev) => prev.filter((_, i) => i !== idx));

  // Every tap of a service tile appends a brand-new line — unlike
  // addToCart, there's no merge-into-an-existing-line case, since two
  // sales of the "same" service still need their own (possibly different)
  // staff assignment; see serviceCart's own doc comment above. `id` is a
  // stable per-line identity (a plain incrementing counter, not the
  // array index) so removing/reordering lines never confuses the scroll
  // target or React's own reconciliation the way an index-based key would.
  const addServiceToCart = (service) => {
    const lineId = nextServiceLineId.current++;
    setServiceCart((prev) => [...prev, { id: lineId, service, staffIds: [], discount_amount: 0, notes: "" }]);
    setScrollTarget({ key: `svc-${lineId}`, ts: Date.now() });
  };

  const updateServiceLine = (idx, patch) => {
    setServiceCart((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const removeServiceLine = (idx) => setServiceCart((prev) => prev.filter((_, i) => i !== idx));

  // Toggles one staff member on/off a service line's staffIds — used by
  // the multi-select chip row (Service.AllowMultipleStaff); the
  // single-staff case uses a plain <select> instead (see the cart render
  // below), since only one person can ever be picked there.
  const toggleServiceLineStaff = (idx, userId) => {
    setServiceCart((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const has = l.staffIds.includes(userId);
      return { ...l, staffIds: has ? l.staffIds.filter((id) => id !== userId) : [...l.staffIds, userId] };
    }));
  };

  const selectedCustomer = useMemo(
    () => (customerId ? customers.find((c) => String(c.id) === customerId) : null),
    [customerId, customers]
  );

  const lineCalcs = useMemo(() => {
    // Pass 1: baseQty/unitPrice/lineGross per line — mirrors resolveLineUnit
    // in order_handler.go. baseQty is quantity as entered (in whichever
    // unit was picked) converted to the product's BASE unit, which is what
    // pricing/tax/stock all actually work in — unitPrice stays per base
    // unit regardless of the chosen unit. Direction matters: a BIGGER
    // alternate unit (e.g. "Carton" of Box) multiplies, a SMALLER one
    // (e.g. "Piece" within a Box) divides — see models.ProductUnit.
    const passOne = cart.map((line) => {
      const productUnit = line.unit_id ? (line.product.product_units || []).find((u) => String(u.unit_id) === line.unit_id) : null;
      const factor = productUnit ? productUnit.conversion_factor : 1;
      const larger = productUnit ? productUnit.is_larger_than_base : true;
      const baseQty = productUnit ? (larger ? line.quantity * factor : line.quantity / factor) : line.quantity;
      const unitPrice = line.product.price * exchangeRate;
      const lineGross = unitPrice * baseQty;
      return { line, productUnit, factor, larger, baseQty, unitPrice, lineGross };
    });

    // Pass 2: bundle offers ("buy N, cheapest one free") are resolved
    // across every unit of every product in the SAME active bundle,
    // pooled together regardless of which cart line each unit came from —
    // this is the live-cart mirror of services.ApplyBundleOffers in
    // order_handler.go, keyed by product.active_bundle_id (see
    // services.CurrentDisplayDiscount / attachDisplayDiscounts). Keyed by
    // this pass's own line index, not product id, since the same product
    // could in principle appear on more than one line (different chosen
    // units).
    const unitsByBundle = {};
    const requiredQtyByBundle = {};
    passOne.forEach((p, idx) => {
      const bundleId = p.line.product.active_bundle_id;
      if (!bundleId) return;
      requiredQtyByBundle[bundleId] = p.line.product.active_bundle_required_quantity;
      const count = Math.round(p.baseQty);
      for (let i = 0; i < count; i++) {
        (unitsByBundle[bundleId] = unitsByBundle[bundleId] || []).push({ lineIndex: idx, unitPrice: p.unitPrice });
      }
    });
    const bundleDiscountByLine = {};
    const bundleFreeCountByLine = {};
    Object.keys(unitsByBundle).forEach((bundleId) => {
      const { discountByLine, freeCountByLine } = applyBundleOffers(unitsByBundle[bundleId], requiredQtyByBundle[bundleId]);
      Object.keys(discountByLine).forEach((idx) => {
        bundleDiscountByLine[idx] = (bundleDiscountByLine[idx] || 0) + discountByLine[idx];
        bundleFreeCountByLine[idx] = (bundleFreeCountByLine[idx] || 0) + freeCountByLine[idx];
      });
    });

    // Pass 3: final per-line discount/tax. A product currently inside an
    // active bundle is governed ONLY by that bundle — never stacked with
    // its own single/quantity-tier discount or any Sale, exactly like
    // EffectiveDiscount's own doc comment on the backend — on top of
    // whatever manual discount_amount the cashier typed into this line,
    // which always still applies regardless of source.
    return passOne.map((p, idx) => {
      const { line, productUnit, factor, larger, baseQty, unitPrice, lineGross } = p;
      const isBundled = !!line.product.active_bundle_id;
      const autoDiscount = isBundled ? (bundleDiscountByLine[idx] || 0) : autoLineDiscount(line.product, baseQty, lineGross, exchangeRate);
      const discount = Math.min((line.discount_amount || 0) + autoDiscount, lineGross);
      const taxableBase = lineGross - discount;
      // A tax-exempt customer (see Customer.IsTaxExempt) skips tax on every
      // line entirely, mirroring the nil-tax-group path CreateOrderHandler
      // takes server-side — same "no tax" behavior a tax-exempt PRODUCT
      // already gets, just triggered by the customer instead.
      const effectiveTaxGroup = selectedCustomer?.is_tax_exempt ? null : line.product.tax_group;
      const { addOn, breakdown } = calculateLineTax(taxableBase, baseQty, exchangeRate, effectiveTaxGroup, branch);
      const lineTotal = taxableBase + addOn;
      const unitPriceInLineUnit = productUnit ? (larger ? unitPrice * factor : unitPrice / factor) : unitPrice;
      const bundleFreeCount = bundleFreeCountByLine[idx] || 0;
      return { unitPrice, unitPriceInLineUnit, baseQty, lineGross, discount, taxAmount: addOn, breakdown, lineTotal, bundleFreeCount };
    });
  }, [cart, exchangeRate, branch, selectedCustomer]);

  // serviceLineCalcs is the service-cart mirror of lineCalcs above — much
  // simpler, since a service line has no unit conversion, no bundle
  // pooling, and always implicit quantity 1 (see
  // checkoutServiceItemPayload's own doc comment in order_handler.go).
  // Mirrors the service-item pass in CreateOrderHandler exactly.
  const serviceLineCalcs = useMemo(() => {
    return serviceCart.map((line) => {
      const lineGross = line.service.price * exchangeRate;
      const discount = Math.min(line.discount_amount || 0, lineGross);
      const taxableBase = lineGross - discount;
      const effectiveTaxGroup = selectedCustomer?.is_tax_exempt ? null : line.service.tax_group;
      const { addOn, breakdown } = calculateLineTax(taxableBase, 1, exchangeRate, effectiveTaxGroup, branch);
      const lineTotal = taxableBase + addOn;
      return { lineGross, discount, taxAmount: addOn, breakdown, lineTotal };
    });
  }, [serviceCart, exchangeRate, branch, selectedCustomer]);

  const totals = useMemo(() => {
    return [...lineCalcs, ...serviceLineCalcs].reduce(
      (acc, lc) => ({
        subtotal: acc.subtotal + lc.lineGross,
        discountTotal: acc.discountTotal + lc.discount,
        taxTotal: acc.taxTotal + lc.taxAmount,
        grandTotal: acc.grandTotal + lc.lineTotal,
      }),
      { subtotal: 0, discountTotal: 0, taxTotal: 0, grandTotal: 0 }
    );
  }, [lineCalcs, serviceLineCalcs]);

  // Aggregated per-tax breakdown across the whole cart (e.g. "VAT 14%" vs.
  // "Service Charge" shown as separate lines) — same shape/helper the
  // historical receipt in OrderDetail.js uses, sourced live from both
  // lineCalcs and serviceLineCalcs.
  const taxBreakdown = useMemo(
    () => aggregateTaxBreakdown([...lineCalcs, ...serviceLineCalcs].map((lc) => lc.breakdown)),
    [lineCalcs, serviceLineCalcs]
  );

  const paidTotal = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const remaining = totals.grandTotal - paidTotal;

  // Total physically received across every row (cash rows use their own
  // received_amount; a non-cash row has no "received vs applied" gap, so it
  // just counts its own amount) — shown as a single summary between the
  // Hold/Complete Sale buttons rather than repeated per row.
  const totalReceived = payments.reduce(
    (sum, p) => sum + (p.method === "cash" && p.received_amount ? Number(p.received_amount) : Number(p.amount) || 0),
    0
  );
  const changeDue = Math.max(0, totalReceived - totals.grandTotal);

  // Which row the quick-cash pad and the "Received" input (down by
  // Hold/Complete Sale) actually operate on — the first row paying by cash.
  // Splitting a sale across more than one cash row is not something this
  // targets; the normal case is exactly one.
  const cashRowIdx = payments.findIndex((row) => row.method === "cash");

  // First press, with nothing picked/typed yet (still just the single
  // default empty row), fills THAT row with the full total in cash instead
  // of appending a second row next to an empty one. Any press after that —
  // once something's actually been entered — appends a new row with
  // whatever's still remaining, same as before.
  const addPaymentRow = () => {
    const newRow = { ...emptyPaymentRow(), amount: remaining > 0 ? remaining.toFixed(2) : "" };
    const nothingEnteredYet = payments.length === 1 && (!payments[0].amount || Number(payments[0].amount) === 0);
    setPayments((prev) => (nothingEnteredYet ? [newRow] : [...prev, newRow]));
  };
  const updatePayment = (idx, patch) => setPayments((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const removePayment = (idx) => setPayments((prev) => prev.filter((_, i) => i !== idx));

  // Tapping a denomination on the quick-cash pad always grows this row's
  // "received" tally. It ALSO fills/grows "Amount" (what's actually applied
  // to the order) for as long as the cashier hasn't typed a different
  // amount by hand — i.e. Amount still exactly matches what's been tapped
  // so far. Once tapped cash covers what's owed, Amount is capped there and
  // any further taps only widen the change due, instead of over-applying
  // to the order total.
  const applyDenominationTap = (idx, value) => {
    setPayments((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        const priorReceived = row.received_amount ? Number(row.received_amount) : 0;
        const newReceived = priorReceived + value;
        const wasAutoTracking = !row.amount || Number(row.amount) === priorReceived;
        const patch = { method: "cash", received_amount: newReceived.toFixed(2) };
        if (wasAutoTracking) {
          const capForRow = remaining + (Number(row.amount) || 0);
          patch.amount = Math.min(newReceived, capForRow).toFixed(2);
        }
        return { ...row, ...patch };
      })
    );
  };

  const resetSale = () => {
    setCart([]);
    setServiceCart([]);
    setCustomerId("");
    setOrderType("retail_sale");
    setTableId("");
    setPayments([emptyPaymentRow()]);
    setResumingOrderId(null);
    setVoucherForfeitPrompt(null);
  };

  // checkVoucherCode previews a code's balance/currency/customer before
  // it's applied — doesn't lock anything server-side (see
  // GetVoucherByCodeHandler), so it can be re-checked freely. On success,
  // auto-fills "Amount" up to whichever is smaller: what's still owed on
  // the sale, or the voucher's own balance — the cashier can still adjust
  // it by hand afterwards (e.g. to apply less and let the setting decide
  // whether that's even allowed at submit time).
  const checkVoucherCode = async (idx) => {
    const code = (payments[idx]?.voucher_code || "").trim();
    if (!code) return;
    setCheckingVoucherIdx(idx);
    try {
      const voucher = await getVoucherByCode(token, code);
      // A voucher that already expired (only ever possible for one issued
      // by a return — see PurchaseVoucher.ExpiresAt's own doc comment) is
      // caught here on the CLIENT rather than left to fail later at
      // submit: CreateOrderHandler rejects it either way, but flagging it
      // right at the "Check" step (same slot voucher_error already uses
      // for "not found") saves the cashier from filling in an amount for
      // a voucher that can never actually be applied.
      const expired = voucher.expires_at && new Date(voucher.expires_at) < new Date();
      setPayments((prev) => prev.map((p, i) => {
        if (i !== idx) return p;
        if (expired) {
          return { ...p, voucher_balance: null, voucher_error: tCheckout("notify_voucher_expired", "This voucher has expired") };
        }
        const capForRow = remaining + (Number(p.amount) || 0);
        const suggested = Math.max(0, Math.min(capForRow, voucher.balance));
        return {
          ...p,
          voucher_balance: voucher.balance,
          voucher_currency_id: voucher.currency_id,
          voucher_error: "",
          amount: p.amount ? p.amount : suggested.toFixed(2),
        };
      }));
    } catch (err) {
      setPayments((prev) => prev.map((p, i) => (i === idx ? { ...p, voucher_balance: null, voucher_error: err.data?.error || tCheckout("notify_voucher_not_found", "Voucher not found") } : p)));
    } finally {
      setCheckingVoucherIdx(-1);
    }
  };

  // Scoped exactly like branch.restrict_held_orders_to_cashier decides
  // resumption rights server-side (see the ownership check in
  // CreateOrderHandler) — when it's on, a cashier shouldn't even see a
  // colleague's held orders in this list, not just be blocked from resuming
  // them.
  const loadHeldOrders = async () => {
    if (!branch) return;
    setLoadingHeld(true);
    try {
      const params = { branch_id: branch.id, status: "held" };
      if (branch.restrict_held_orders_to_cashier) params.cashier_id = shift.cashier_id;
      const orders = await getOrders(token, params);
      setHeldOrders(orders || []);
    } catch (err) {
      notify.error(tCheckout("notify_load_held_failed", "Failed to load held orders"));
    } finally {
      setLoadingHeld(false);
    }
  };

  const openHeldOrders = () => {
    setShowHeldOrders(true);
    loadHeldOrders();
  };

  // Rebuilds the cart from a held order's snapshot. The product objects come
  // from the already-loaded `products` list (not order.items[].product)
  // because that's the copy that carries product_units/tax_group — the
  // fields the unit selector and lineCalcs need, which GetOrderByIDHandler
  // doesn't preload onto the item's snapshot.
  const handleResumeOrder = async (orderSummary) => {
    try {
      const order = await getOrderById(token, orderSummary.id);
      const rebuiltCart = (order.items || []).map((item) => {
        const product = products.find((p) => p.id === item.product_id) || item.product;
        return {
          product,
          quantity: item.display_quantity || item.quantity,
          unit_id: item.unit_id ? String(item.unit_id) : "",
          discount_amount: item.discount_amount || 0,
          notes: item.notes || "",
        };
      });
      setCart(rebuiltCart);
      // Same idea as rebuiltCart above: prefer the already-loaded `services`
      // list entry (carries tax_group/staff_assignments, which
      // serviceLineCalcs and the staff picker both need) and only fall back
      // to the order's own snapshot — a bare {id, name, price} shape — for
      // a service that's since been deleted.
      const rebuiltServiceCart = (order.service_items || []).map((si) => {
        // The fallback below is effectively unreachable in practice —
        // DeleteServiceHandler refuses to delete a service any
        // OrderServiceItem still references, held orders included — but is
        // kept as a defensive last resort. si.price is a snapshot already
        // multiplied by the ORIGINAL sale's exchange rate (see
        // OrderServiceItem.Price's own doc comment); divided back out here
        // so serviceLineCalcs' own "* exchangeRate" re-multiplication (by
        // the CURRENT rate) lands on the right number instead of
        // double-converting.
        const service = services.find((s) => s.id === si.service_id) || {
          id: si.service_id, name: si.service_name, price: si.price / (order.exchange_rate || 1),
          tax_group: null, allow_multiple_staff: (si.staff || []).length > 1, staff_assignments: [],
        };
        return {
          id: nextServiceLineId.current++,
          service,
          staffIds: (si.staff || []).map((row) => row.user_id),
          discount_amount: si.discount_amount || 0,
          notes: si.notes || "",
        };
      });
      setServiceCart(rebuiltServiceCart);
      setCustomerId(order.customer_id ? String(order.customer_id) : "");
      setOrderType(order.order_type || "retail_sale");
      setTableId(order.table_id ? String(order.table_id) : "");
      setPayments([{ method: "cash", amount: "", received_amount: "" }]);
      setResumingOrderId(order.id);
      setShowHeldOrders(false);
      notify.success(tCheckout("notify_resuming", "Resuming order #{id}").replace("{id}", order.id));
    } catch (err) {
      notify.error(err.data?.error || err.message || tCheckout("notify_resume_failed", "Failed to load that held order"));
    }
  };

  // Cancelling a held order (see CancelHeldOrderHandler) releases whatever
  // stock it had reserved back to sellable — allowed for whoever held it,
  // this branch's manager, or an admin; the backend is the actual
  // authority on that, this just surfaces whatever it says no to.
  const handleCancelHeldOrder = async (order, reason) => {
    setCancellingId(order.id);
    try {
      await cancelOrder(token, order.id, { reason });
      notify.success(tCheckout("notify_order_cancelled", "Order #{id} cancelled").replace("{id}", order.id));
      if (resumingOrderId === order.id) resetSale();
      loadHeldOrders();
    } catch (err) {
      notify.error(err.data?.error || err.message || tCheckout("notify_cancel_held_failed", "Failed to cancel that held order"));
    } finally {
      setCancellingId(null);
    }
  };

  // serviceItemsPayload builds the service_items array both
  // handleHoldSale and submitCheckout send — see
  // checkoutServiceItemPayload in order_handler.go.
  const serviceItemsPayload = () => serviceCart.map((l) => ({
    service_id: l.service.id,
    staff_ids: l.staffIds,
    discount_amount: l.discount_amount || 0,
    notes: l.notes || "",
  }));

  // Every service line needs at least one staff member picked before the
  // sale can go through — the backend rejects it either way (see
  // CreateOrderHandler's service-item pass), but catching it here first
  // saves a round trip and points the cashier at which line is missing it.
  const validateServiceStaffPicked = () => {
    if (serviceCart.some((l) => l.staffIds.length === 0)) {
      notify.error(tCheckout("validation_service_staff_required", "Pick a staff member for every service in the cart"));
      return false;
    }
    return true;
  };

  // Unlike a product line's discount (silently clamped in the preview —
  // see lineCalcs above, since the backend itself only clamps once an
  // AUTOMATIC bundle/tier discount is added on top of an already-valid
  // manual amount), a service line has no automatic discount source at
  // all: CreateOrderHandler REJECTS the whole sale outright if
  // discount_amount alone exceeds the line's gross (see order_handler.go).
  // serviceLineCalcs clamps purely for a sane live total to DISPLAY, so
  // this check has to run separately before submit — otherwise the
  // cashier sees a perfectly normal-looking $0 line total right up until
  // "Complete Sale" abruptly 400s with no visible cause.
  const validateServiceDiscounts = () => {
    const over = serviceCart.find((l) => (l.discount_amount || 0) > l.service.price * exchangeRate);
    if (over) {
      notify.error(tCheckout("validation_service_discount_exceeds_price", "{name}'s discount can't exceed its price").replace("{name}", over.service.name));
      return false;
    }
    return true;
  };

  // A dine-in order needs a table — CreateOrderHandler rejects one without
  // it (see order_handler.go) just as strictly, but catching it here first
  // saves a round trip. Any other order type never sends table_id at all
  // (see the payload builders below), so this only ever matters when
  // orderType is "dine_in".
  const validateTableSelected = () => {
    if (orderType === "dine_in" && !tableId) {
      notify.error(tCheckout("validation_table_required", "Pick a table for this dine-in order"));
      return false;
    }
    return true;
  };

  const handleHoldSale = async () => {
    if (cart.length === 0 && serviceCart.length === 0) {
      notify.error(tCheckout("validation_cart_empty", "Add at least one product to the cart"));
      return;
    }
    if (!validateServiceStaffPicked()) return;
    if (!validateServiceDiscounts()) return;
    if (!validateTableSelected()) return;

    const payload = {
      hold: true,
      resume_order_id: resumingOrderId || 0,
      customer_id: customerId ? Number(customerId) : 0,
      order_type: orderType,
      table_id: orderType === "dine_in" && tableId ? Number(tableId) : 0,
      items: cart.map((l) => ({
        product_id: l.product.id,
        unit_id: l.unit_id ? Number(l.unit_id) : 0,
        quantity: l.quantity,
        discount_amount: l.discount_amount || 0,
        notes: l.notes || "",
      })),
      service_items: serviceItemsPayload(),
      payments: [],
    };

    setHolding(true);
    try {
      const order = await createOrder(token, payload);
      notify.success(tCheckout("notify_order_held", "Order held — #{id}").replace("{id}", order.id));
      resetSale();
    } catch (err) {
      notify.error(err.data?.error || err.message || tCheckout("notify_hold_failed", "Failed to hold the order"));
    } finally {
      setHolding(false);
    }
  };

  // submitCheckout does the actual createOrder call, from whichever
  // `payments` snapshot the caller hands it — handleCompleteSale passes
  // the live state, confirmForfeitRemainder below passes an updated copy
  // with forfeit_remainder just set on the voucher row, since setState is
  // async and the resubmit can't wait a render for it to land.
  const submitCheckout = async (paymentsToUse) => {
    const payload = {
      resume_order_id: resumingOrderId || 0,
      customer_id: customerId ? Number(customerId) : 0,
      order_type: orderType,
      table_id: orderType === "dine_in" && tableId ? Number(tableId) : 0,
      items: cart.map((l) => ({
        product_id: l.product.id,
        unit_id: l.unit_id ? Number(l.unit_id) : 0,
        quantity: l.quantity,
        discount_amount: l.discount_amount || 0,
        notes: l.notes || "",
      })),
      service_items: serviceItemsPayload(),
      payments: paymentsToUse
        .filter((p) => Number(p.amount) > 0)
        .map((p) => ({
          method: p.method,
          amount: Number(p.amount),
          received_amount: p.received_amount ? Number(p.received_amount) : Number(p.amount),
          reference: p.reference || "",
          voucher_code: p.method === "voucher" ? (p.voucher_code || "").trim() : "",
          forfeit_remainder: p.method === "voucher" ? !!p.forfeit_remainder : false,
        })),
    };

    setSubmitting(true);
    try {
      const order = await createOrder(token, payload);
      // A real payment gateway is active for this currency and the sale had
      // exactly one Card payment row — CreateOrderHandler decided that
      // server-side (see needsGateway in order_handler.go), never the
      // frontend. The order exists (stock reserved, nothing decremented
      // yet) but isn't paid until the gateway confirms, so this does NOT
      // resetSale()/navigate() like a normal completed sale — it opens the
      // hosted payment page and switches to the waiting view instead; see
      // the polling effect below for how that view resolves.
      if (order.status === "awaiting_payment_confirmation") {
        const tx = (order.payment_transactions || [])[0];
        setPendingPayment({ order, transaction: tx || null });
        if (tx?.hosted_payment_url) {
          window.open(tx.hosted_payment_url, "_blank", "noopener,noreferrer");
        }
        return;
      }
      notify.success(tCheckout("notify_sale_complete", "Sale complete — Order #{id}").replace("{id}", order.id));
      resetSale();
      navigate(`/orders/${order.id}`);
    } catch (err) {
      if (err.data?.code === "voucher_full_redemption_required") {
        const voucherIdx = paymentsToUse.findIndex((p) => p.method === "voucher");
        setVoucherForfeitPrompt({ paymentIndex: voucherIdx, balance: err.data.voucher_balance, shortfall: err.data.shortfall });
        return;
      }
      notify.error(err.data?.error || err.message || tCheckout("notify_sale_failed", "Failed to complete the sale"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteSale = () => {
    if (cart.length === 0 && serviceCart.length === 0) {
      notify.error(tCheckout("validation_cart_empty", "Add at least one product to the cart"));
      return;
    }
    if (!validateServiceStaffPicked()) return;
    if (!validateServiceDiscounts()) return;
    if (!validateTableSelected()) return;
    if (Math.abs(remaining) > 0.01) {
      const word = remaining > 0 ? tCheckout("label_remaining", "remaining") : tCheckout("label_over", "over");
      notify.error(tCheckout("validation_payments_mismatch", "Payments must add up to the total ({amount})").replace("{amount}", `${money(remaining)} ${word}`));
      return;
    }
    submitCheckout(payments);
  };

  // The confirmed forfeiture-choice flow: "Forfeit the remainder" resubmits
  // the SAME sale with forfeit_remainder set on the voucher row (backend
  // then zeroes what's left of the voucher); "Add more items" just closes
  // the prompt so the cashier can grow the cart and try again — no change
  // needed here for that path, since a bigger cart naturally raises
  // "remaining" and the cashier can then raise the voucher row's Amount
  // back up towards its full balance.
  const confirmForfeitRemainder = () => {
    if (!voucherForfeitPrompt || voucherForfeitPrompt.paymentIndex < 0) return;
    const idx = voucherForfeitPrompt.paymentIndex;
    const updated = payments.map((p, i) => (i === idx ? { ...p, forfeit_remainder: true } : p));
    setPayments(updated);
    setVoucherForfeitPrompt(null);
    submitCheckout(updated);
  };

  // Polls GetOrderPaymentStatusHandler every ~3s while pendingPayment is
  // set — the authoritative signal (per the plan, never the only-ever-once
  // webhook alone, since N-Genius delivers it exactly once with no retries
  // and Paymob's own HMAC verification is deliberately unimplemented — see
  // gateway_paymob.go). order_status flips to "completed" the instant
  // FinalizePendingGatewayOrder runs (webhook OR the backend's own 30s
  // reconciliation loop, whichever gets there first) and to "cancelled"
  // if the reconciliation loop auto-expired it past payment_timeout_minutes
  // — either way this effect is what notices, not a websocket push alone.
  // Re-runs only when the order id itself changes (not on every
  // poll-driven transaction update), so it never restarts its own interval.
  useEffect(() => {
    if (!pendingPayment?.order?.id) return undefined;
    const orderId = pendingPayment.order.id;
    let stopped = false;

    const poll = async () => {
      try {
        const res = await getOrderPaymentStatus(token, orderId);
        if (stopped) return;
        if (res.order_status === "completed") {
          notify.success(tCheckout("notify_sale_complete", "Sale complete — Order #{id}").replace("{id}", orderId));
          setPendingPayment(null);
          resetSale();
          navigate(`/orders/${orderId}`);
          return;
        }
        if (res.order_status === "cancelled") {
          notify.error(tCheckout("notify_payment_order_cancelled", "This order was cancelled before payment was confirmed"));
          setPendingPayment(null);
          return;
        }
        setPendingPayment((prev) => (prev && prev.order.id === orderId ? { ...prev, transaction: res.payment_transaction } : prev));
      } catch (err) {
        // A transient network hiccup shouldn't spam the cashier with an
        // error every 3 seconds — just try again on the next tick.
      }
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => { stopped = true; clearInterval(interval); };
  }, [pendingPayment?.order?.id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const reopenHostedPaymentPage = () => {
    if (pendingPayment?.transaction?.hosted_payment_url) {
      window.open(pendingPayment.transaction.hosted_payment_url, "_blank", "noopener,noreferrer");
    }
  };

  // Only offered once the latest attempt has actually failed/expired (see
  // RetryOrderPaymentHandler's own precondition) — the cart/reservation are
  // untouched, only a new PaymentTransaction + hosted session are created.
  const handleRetryPayment = async () => {
    if (!pendingPayment) return;
    setPayingAction(true);
    try {
      const tx = await retryOrderPayment(token, pendingPayment.order.id);
      setPendingPayment((prev) => (prev ? { ...prev, transaction: tx } : prev));
      if (tx?.hosted_payment_url) {
        window.open(tx.hosted_payment_url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      notify.error(err.data?.error || err.message || tCheckout("notify_retry_payment_failed", "Failed to start a new payment attempt"));
    } finally {
      setPayingAction(false);
    }
  };

  // The only path that actually releases the stock reservation and cancels
  // the order while it's still awaiting gateway confirmation — clears just
  // the waiting/payment state, never the cart (see pendingPayment's own
  // doc comment above), so the cashier can pick a different method and try
  // again with the same items.
  const handleCancelPendingPayment = async () => {
    if (!pendingPayment) return;
    setPayingAction(true);
    try {
      await cancelPendingPayment(token, pendingPayment.order.id);
      setPendingPayment(null);
      notify.success(tCheckout("notify_payment_cancelled", "Payment cancelled — your cart is still here"));
    } catch (err) {
      notify.error(err.data?.error || err.message || tCheckout("notify_cancel_payment_failed", "Failed to cancel this payment"));
    } finally {
      setPayingAction(false);
    }
  };

  if (loadingShift) {
    return <div className="p-8 text-center text-gray-400">{t("loading_text", "Loading…")}</div>;
  }

  if (!shift) {
    return (
      <div className="p-4 md:p-8 max-w-3xl mx-auto">
        <OpenShiftPanel branches={branches} onOpened={setShift} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1800px] mx-auto transition-all duration-300 lg:h-full lg:flex lg:flex-col lg:overflow-hidden">
      {/* Hides the scrollbar on the horizontally-swipeable product pager and
          category chip bar below, while leaving the drag/scroll gesture
          itself fully working — this is purely cosmetic. */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>

      {showCloseShift && (
        <CloseShiftModal
          shift={shift}
          onClose={() => setShowCloseShift(false)}
          onClosed={() => { setShowCloseShift(false); setShift(null); loadShift(); }}
        />
      )}

      {showHeldOrders && (
        <HeldOrdersModal
          orders={heldOrders}
          loading={loadingHeld}
          onClose={() => setShowHeldOrders(false)}
          onResume={handleResumeOrder}
          onCancel={handleCancelHeldOrder}
          cancellingId={cancellingId}
        />
      )}

      <PageHeader className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4 border-b border-gray-100 pb-6 lg:shrink-0">
        <div className="flex items-center gap-4 shrink-0">
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Checkout size={36} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{branch?.name}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">
              {tCheckout("shift_info_line", "Shift opened {time} — opening balance {amount}").replace("{time}", new Date(shift.opened_at).toLocaleString()).replace("{amount}", money(shift.opening_balance))}
            </p>
          </div>
        </div>

        {/* Moved up here (next to the title) instead of sitting above the
            product grid, freeing that area for products — see
            productPages below. */}
        <div className="flex flex-col sm:flex-row gap-3 lg:flex-1 lg:max-w-xl">
          <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder={tCheckout("search_placeholder", "Search products, SKU, barcode...")} />
          {/* Category filtering already has its own dedicated, always-visible
              chip bar above the product grid, so this slot in the header is
              used for the Customer picker instead (moved up from the cart
              panel below, freeing space there). */}
          <div className="sm:w-56 shrink-0">
            <SelectBox options={customerOptions} value={customerId} onChange={setCustomerId} label={tCheckout("field_customer_optional_label", "Customer (optional)")} />
            {selectedCustomer?.is_tax_exempt && (
              <p className="text-[10px] font-bold text-emerald-600 mt-1">{tCheckout("tax_exempt_hint", "Tax exempt — no tax charged")}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <Button variant="outline" icon={ICONS.Orders} onClick={openHeldOrders}>{t("btn_held_orders", "Held Orders")}</Button>
          <Button variant="outline" icon={ICONS.Vouchers} onClick={() => setShowSellVoucher(true)}>{tCheckout("btn_sell_voucher", "Sell Voucher")}</Button>
          <Button variant="outline" icon={ICONS.Orders} onClick={() => navigate("/orders")}>{tCheckout("btn_orders", "Orders")}</Button>
          <Button variant="danger" icon={ICONS.Shifts} onClick={() => setShowCloseShift(true)}>{tCheckout("btn_close_shift", "Close Shift")}</Button>
        </div>
      </PageHeader>

      {showSellVoucher && (
        <SellVoucherModal
          customerOptions={customerOptions}
          onClose={() => setShowSellVoucher(false)}
          onSold={() => {}}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:flex-1 lg:min-h-0">
        {/* Product picker */}
        <div className="lg:col-span-2 lg:self-start">
          {/* Products/Services tab switcher — only rendered at all when
              this branch's business_type is service-capable (see
              SERVICE_CAPABLE_BUSINESS_TYPES above); every other branch
              keeps the exact pre-Phase-1b product picker, unchanged. */}
          {canSellServices && (
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setPickerTab("products")}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${
                  pickerTab === "products" ? "bg-[var(--primary-color)] text-white" : "bg-white text-gray-500 border border-gray-100 hover:border-[var(--primary-color)]"
                }`}
              >
                {tCheckout("tab_products", "Products")}
              </button>
              <button
                onClick={() => setPickerTab("services")}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${
                  pickerTab === "services" ? "bg-[var(--primary-color)] text-white" : "bg-white text-gray-500 border border-gray-100 hover:border-[var(--primary-color)]"
                }`}
              >
                {tCheckout("tab_services", "Services")}
              </button>
            </div>
          )}

          {/* Quick category chips — services now share the SAME Category
              table Products do (see models.Category's doc comment), so the
              exact same categoryFilter state/chip bar filters whichever
              grid is currently showing (filteredProducts or
              filteredServices both apply it) — picking a chip here or the
              dropdown up in the header keeps both in sync. Horizontally
              draggable/scrollable itself, same as the product pager below,
              to reach categories that don't fit. */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-3">
            <button
              onClick={() => setCategoryFilter("")}
              className={`shrink-0 px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-colors ${
                categoryFilter === "" ? "bg-[var(--primary-color)] text-white" : "bg-white text-gray-500 border border-gray-100 hover:border-[var(--primary-color)]"
              }`}
            >
              {tCheckout("category_all", "All")}
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategoryFilter(String(c.id))}
                className={`shrink-0 px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-colors ${
                  categoryFilter === String(c.id) ? "bg-[var(--primary-color)] text-white" : "bg-white text-gray-500 border border-gray-100 hover:border-[var(--primary-color)]"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          {pickerTab === "services" && canSellServices ? (
            /* Services grid — no paging (a salon/beauty center's service
               list is normally short enough not to need it), just a plain
               wrapping grid filtered by the shared category chips above
               plus the search box. Tapping a tile adds a brand-new line
               straight away (see addServiceToCart); staff is picked
               afterwards, right on the cart line below. */
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {filteredServices.length === 0 && (
                <p className="col-span-full text-center text-gray-400 italic py-10">{tCheckout("no_services_match", "No services match.")}</p>
              )}
              {filteredServices.map((s) => (
                <button
                  key={s.id}
                  onClick={() => addServiceToCart(s)}
                  className="text-start bg-white rounded-2xl border border-gray-100 shadow-sm p-2.5 hover:border-[var(--primary-color)] hover:shadow-md transition-all"
                >
                  <div className="w-full aspect-square rounded-xl bg-gray-50 mb-1.5 flex items-center justify-center overflow-hidden">
                    {s.image_url ? (
                      <img
                        src={getAssetUrl(s.image_url)}
                        alt={s.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ICONS.Services className="w-6 h-6 text-gray-300" />
                    )}
                  </div>
                  <div className="font-bold text-gray-800 text-xs mb-0.5 line-clamp-1">{s.name}</div>
                  <div className="text-[9px] text-gray-400 font-mono mb-1">{tCheckout("duration_minutes_value", "{minutes} min").replace("{minutes}", s.duration_minutes)}</div>
                  <div className="font-mono font-bold text-xs text-[var(--primary-color)]">{money(s.price * exchangeRate)} {currencyLabel}</div>
                </button>
              ))}
            </div>
          ) : (
            <>
              {/* Paged 8-at-a-time (2 rows of 4 on sm+) instead of a tall
                  vertically-scrolling grid — each page is a full-width snap
                  point; dragging/swiping the strip left reveals the next page,
                  right goes back, down to the first (see productPages,
                  handleProductPagerScroll above). */}
              <div
                ref={productPagerRef}
                onScroll={handleProductPagerScroll}
                className="flex items-start overflow-x-auto snap-x snap-mandatory no-scrollbar"
              >
                {productPages.map((page, pageIdx) => (
                  <div key={pageIdx} className="w-full shrink-0 snap-start grid grid-cols-2 sm:grid-cols-5 gap-3 pe-1">
                    {page.length === 0 && (
                      <p className="col-span-full text-center text-gray-400 italic py-10">{tCheckout("no_products_match", "No products match.")}</p>
                    )}
                    {page.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => addToCart(p)}
                        className="text-start bg-white rounded-2xl border border-gray-100 shadow-sm p-2.5 hover:border-[var(--primary-color)] hover:shadow-md transition-all"
                      >
                        <div className="w-full aspect-square rounded-xl bg-gray-50 mb-1.5 flex items-center justify-center overflow-hidden">
                          {p.image_url ? (
                            <img
                              src={getAssetUrl(p.image_url)}
                              alt={p.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <ICONS.Products className="w-6 h-6 text-gray-300" />
                          )}
                        </div>
                        <div className="font-bold text-gray-800 text-xs mb-0.5 line-clamp-1">{p.name}</div>
                        <div className="text-[9px] text-gray-400 font-mono mb-1">{p.sku}</div>
                        {p.discount_source === "bundle" ? (
                          <div className="flex items-center gap-1">
                            <span className="font-mono font-bold text-xs text-[var(--primary-color)]">{money(p.price * exchangeRate)} {currencyLabel}</span>
                            <span className="px-1.5 py-0.5 rounded-full text-[8px] font-black uppercase bg-purple-50 text-purple-600">{tCheckout("badge_bundle", "Bundle")}</span>
                          </div>
                        ) : p.discounted_price != null && p.discounted_price < p.price ? (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-mono text-[10px] text-gray-400 line-through">{money(p.price * exchangeRate)}</span>
                            <span className="font-mono font-bold text-xs text-green-600">{money(p.discounted_price * exchangeRate)} {currencyLabel}</span>
                          </div>
                        ) : (
                          <div className="font-mono font-bold text-xs text-[var(--primary-color)]">{money(p.price * exchangeRate)} {currencyLabel}</div>
                        )}
                      </button>
                    ))}
                  </div>
                ))}
              </div>

              {/* Passive page-position indicator — not clickable, just shows
                  where you are and how many pages there are; the pages
                  themselves are only ever reached by dragging/swiping. */}
              {productPages.length > 1 && (
                <div className="flex items-center justify-center gap-1.5 mt-3">
                  {productPages.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-full transition-all ${
                        i === currentProductPage ? "w-5 bg-[var(--primary-color)]" : "w-1.5 bg-gray-200"
                      }`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Cart */}
        <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-5 flex flex-col max-h-[85vh] lg:max-h-none lg:h-full lg:min-h-0">
          <div className="flex items-center justify-between mb-3 lg:shrink-0">
            <h3 className="font-black text-gray-800">{tCheckout("cart_title", "Cart")}</h3>
            <span className="text-xs font-bold text-gray-400 bg-gray-50 px-2.5 py-1 rounded-full">
              {cart.length + serviceCart.length} {cart.length + serviceCart.length === 1 ? tCheckout("cart_item_singular", "item") : tCheckout("cart_item_plural", "items")} · {cart.reduce((sum, l) => sum + Number(l.quantity || 0), 0) + serviceCart.length} {tCheckout("label_qty_suffix", "qty")}
            </span>
          </div>

          {resumingOrderId && (
            <div className="flex items-center justify-between gap-2 mb-3 px-3 py-2 rounded-xl bg-amber-50 text-amber-700 text-xs font-bold lg:shrink-0">
              <span>{tCheckout("resuming_order_banner", "Resuming held order #{id}").replace("{id}", resumingOrderId)}</span>
              <button onClick={resetSale} className="underline hover:no-underline">{t("btn_cancel", "Cancel")}</button>
            </div>
          )}

          {/* Order Type + Table — the Table picker only ever renders once
              "Dine-in" is actually picked (see ORDER_TYPE_OPTIONS above,
              itself gated by canUseTables/TABLE_CAPABLE_BUSINESS_TYPES), so
              a retail-only branch's Checkout looks exactly as it always
              did. */}
          <div className="grid grid-cols-2 gap-2 mb-3 lg:shrink-0">
            <SelectBox
              label={tCheckout("field_order_type_label", "Order Type")}
              options={ORDER_TYPE_OPTIONS}
              value={orderType}
              onChange={(val) => { setOrderType(val); if (val !== "dine_in") setTableId(""); }}
            />
            {orderType === "dine_in" && (
              <SelectBox
                label={tCheckout("field_table_label", "Table")}
                options={tableOptions}
                value={tableId}
                onChange={setTableId}
              />
            )}
          </div>

          <div className="flex-1 lg:min-h-0 overflow-y-auto space-y-3 mb-4">
            {cart.length === 0 && serviceCart.length === 0 && <p className="text-gray-400 text-sm italic text-center py-8">{tCheckout("cart_empty_hint", "Tap a product to add it")}</p>}
            {cart.map((line, idx) => {
              const altUnits = line.product.product_units || [];
              const canPickUnit = altUnits.length > 0;
              const canFraction = !!line.product.allows_fractional_qty;
              const step = canFraction ? 0.5 : 1;
              const unitLabel = line.unit_id
                ? altUnits.find((u) => String(u.unit_id) === line.unit_id)?.unit?.name
                : line.product.unit?.name;
              const lineKey = `${line.product.id}-${line.unit_id || "base"}`;
              return (
                <div
                  key={lineKey}
                  ref={(el) => { lineRefs.current[lineKey] = el; }}
                  className="border border-gray-100 rounded-2xl p-3"
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <div className="font-bold text-sm text-gray-800 truncate">{line.product.name}</div>
                      <div className="text-[10px] text-gray-400 font-mono">
                        {money(lineCalcs[idx]?.unitPriceInLineUnit)} {currencyLabel} / {unitLabel || tCheckout("unit_fallback", "unit")}
                      </div>
                      {lineCalcs[idx]?.bundleFreeCount > 0 && (
                        <div className="text-[10px] font-black text-purple-600 uppercase mt-0.5">
                          {tCheckout("bundle_free_badge", "🎁 {count} free (Bundle Offer)").replace("{count}", lineCalcs[idx].bundleFreeCount)}
                        </div>
                      )}
                    </div>
                    <button onClick={() => removeLine(idx)} className="text-gray-300 hover:text-red-500 shrink-0">
                      <ICONS.Close className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    {canPickUnit && (
                      <select
                        value={line.unit_id || ""}
                        onChange={(e) => updateLine(idx, { unit_id: e.target.value })}
                        className="w-16 shrink-0 text-[11px] px-1 py-1.5 rounded-lg border border-gray-100 bg-gray-50/50 focus:outline-none"
                      >
                        <option value="">{line.product.unit?.name || tCheckout("base_unit_option", "Base unit")}</option>
                        {altUnits.map((u) => (
                          <option key={u.unit_id} value={u.unit_id}>{u.unit?.name}</option>
                        ))}
                      </select>
                    )}
                    <div className="flex items-center border border-gray-100 rounded-xl overflow-hidden shrink-0">
                      <button
                        onClick={() => updateLine(idx, { quantity: Math.max(step, +(line.quantity - step).toFixed(2)) })}
                        className="w-7 h-7 text-gray-500 hover:bg-gray-50"
                      >
                        −
                      </button>
                      {canFraction ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={line.quantity}
                          onChange={(e) => updateLine(idx, { quantity: Math.max(0.01, Number(e.target.value) || 0) })}
                          className="w-14 text-center text-sm font-mono bg-transparent focus:outline-none"
                        />
                      ) : (
                        <span className="w-9 text-center text-sm font-mono">{line.quantity}</span>
                      )}
                      <button
                        onClick={() => updateLine(idx, { quantity: +(line.quantity + step).toFixed(2) })}
                        className="w-7 h-7 text-gray-500 hover:bg-gray-50"
                      >
                        +
                      </button>
                    </div>
                    <input
                      type="number"
                      value={line.discount_amount || ""}
                      onChange={(e) => updateLine(idx, { discount_amount: Number(e.target.value) || 0 })}
                      placeholder={tCheckout("discount_placeholder", "Discount")}
                      className="w-14 min-w-0 shrink text-xs px-2 py-1 rounded-lg border border-gray-100 bg-gray-50/50 focus:outline-none focus:border-[var(--primary-color)]"
                    />
                    <span className="ml-auto shrink-0 font-mono font-bold text-sm text-gray-800">{money(lineCalcs[idx]?.lineTotal)}</span>
                  </div>
                  {(lineCalcs[idx]?.breakdown || []).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-50 space-y-0.5">
                      {lineCalcs[idx].breakdown.map((taxItem, i) => (
                        <div key={i} className="flex justify-between text-[10px] text-gray-400">
                          <span>
                            {taxItem.tax_name} ({taxRateLabel(taxItem, currencyLabel)}){taxItem.is_price_inclusive ? " · incl." : ""}
                          </span>
                          <span className="font-mono">{money(taxItem.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Service lines — a separate block from the product lines
                above, right below them in the same scrollable list. Each
                line picks its own staff AFTER being added (see
                addServiceToCart), never a quantity/unit like a product
                line, since a service always sells at implicit quantity 1
                (see checkoutServiceItemPayload's own doc comment in
                order_handler.go). */}
            {serviceCart.map((line, idx) => {
              const eligibleStaff = (line.service.staff_assignments || []).filter((a) => a.is_active);
              const lineKey = `svc-${line.id}`;
              return (
                <div
                  key={lineKey}
                  ref={(el) => { lineRefs.current[lineKey] = el; }}
                  className="border border-gray-100 rounded-2xl p-3 bg-purple-50/20"
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <ICONS.Services className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                        <span className="font-bold text-sm text-gray-800 truncate">{line.service.name}</span>
                      </div>
                      <div className="text-[10px] text-gray-400 font-mono">
                        {money(serviceLineCalcs[idx]?.lineGross)} {currencyLabel} · {tCheckout("badge_service", "Service")}
                      </div>
                    </div>
                    <button onClick={() => removeServiceLine(idx)} className="text-gray-300 hover:text-red-500 shrink-0">
                      <ICONS.Close className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="mt-2">
                    <label className="text-[10px] font-bold uppercase text-gray-400">{tCheckout("field_staff_label", "Staff")}</label>
                    {eligibleStaff.length === 0 ? (
                      <p className="text-[11px] text-red-500 font-bold mt-1">{tCheckout("no_staff_assigned_hint", "No staff are assigned to perform this service yet")}</p>
                    ) : line.service.allow_multiple_staff ? (
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {eligibleStaff.map((a) => {
                          const active = line.staffIds.includes(a.user_id);
                          return (
                            <button
                              key={a.user_id}
                              type="button"
                              onClick={() => toggleServiceLineStaff(idx, a.user_id)}
                              className={`px-2 py-1 rounded-lg text-[10px] font-bold border transition-colors ${
                                active ? "bg-[var(--primary-color)] text-white border-transparent" : "bg-gray-50 text-gray-500 border-gray-100 hover:border-[var(--primary-color)]"
                              }`}
                            >
                              {a.user?.username}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <select
                        value={line.staffIds[0] || ""}
                        onChange={(e) => updateServiceLine(idx, { staffIds: e.target.value ? [Number(e.target.value)] : [] })}
                        className="w-full mt-1 text-xs px-2 py-1.5 rounded-lg border border-gray-100 bg-gray-50/50 focus:outline-none"
                      >
                        <option value="">{tCheckout("select_staff_placeholder", "Select staff")}</option>
                        {eligibleStaff.map((a) => (
                          <option key={a.user_id} value={a.user_id}>{a.user?.username}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="number"
                      value={line.discount_amount || ""}
                      onChange={(e) => updateServiceLine(idx, { discount_amount: Number(e.target.value) || 0 })}
                      placeholder={tCheckout("discount_placeholder", "Discount")}
                      className="w-20 min-w-0 shrink text-xs px-2 py-1 rounded-lg border border-gray-100 bg-gray-50/50 focus:outline-none focus:border-[var(--primary-color)]"
                    />
                    <span className="ml-auto shrink-0 font-mono font-bold text-sm text-gray-800">{money(serviceLineCalcs[idx]?.lineTotal)}</span>
                  </div>
                  {(serviceLineCalcs[idx]?.breakdown || []).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-50 space-y-0.5">
                      {serviceLineCalcs[idx].breakdown.map((taxItem, i) => (
                        <div key={i} className="flex justify-between text-[10px] text-gray-400">
                          <span>
                            {taxItem.tax_name} ({taxRateLabel(taxItem, currencyLabel)}){taxItem.is_price_inclusive ? " · incl." : ""}
                          </span>
                          <span className="font-mono">{money(taxItem.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="border-t border-gray-100 pt-4 space-y-3 lg:shrink-0">
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-gray-500"><span>{tCheckout("label_subtotal", "Subtotal")}</span><span className="font-mono">{money(totals.subtotal)}</span></div>
              <div className="flex justify-between text-gray-500"><span>{tCheckout("discount_placeholder", "Discount")}</span><span className="font-mono text-red-500">{totals.discountTotal > 0 ? `-${money(totals.discountTotal)}` : "—"}</span></div>
              <div className="flex justify-between text-gray-500"><span>{tCheckout("label_tax", "Tax")}</span><span className="font-mono">{money(totals.taxTotal)}</span></div>
              {taxBreakdown.length > 0 && (
                <div className="ps-3 space-y-0.5">
                  {taxBreakdown.map((taxItem, i) => (
                    <div key={i} className="flex justify-between text-[11px] text-gray-400">
                      <span>
                        {taxItem.tax_name} ({taxRateLabel(taxItem, currencyLabel)}){taxItem.is_price_inclusive ? " · incl." : ""}
                      </span>
                      <span className="font-mono">{money(taxItem.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between text-lg font-black text-gray-900 pt-1 border-t border-gray-50">
                <span>{tCheckout("label_total", "Total")}</span><span className="font-mono">{money(totals.grandTotal)} {currencyLabel}</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase font-bold text-gray-400 tracking-wide">{tCheckout("label_payments", "Payments")}</span>
                <button onClick={addPaymentRow} className="text-xs font-bold text-[var(--primary-color)] hover:underline">{tCheckout("btn_add_payment", "+ Add")}</button>
              </div>
              {payments.map((p, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <select
                      value={p.method}
                      onChange={(e) => updatePayment(idx, {
                        method: e.target.value, received_amount: "",
                        voucher_code: "", voucher_balance: null, voucher_error: "", forfeit_remainder: false,
                      })}
                      className="text-xs px-2 py-2 rounded-lg border border-gray-100 bg-gray-50/50 focus:outline-none"
                    >
                      {PAYMENT_METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <input
                      type="number"
                      value={p.amount}
                      onChange={(e) => updatePayment(idx, { amount: e.target.value })}
                      placeholder={tCheckout("amount_placeholder", "Amount")}
                      className="flex-1 text-xs px-2 py-2 rounded-lg border border-gray-100 bg-gray-50/50 focus:outline-none focus:border-[var(--primary-color)]"
                    />
                    {payments.length > 1 && (
                      <button onClick={() => removePayment(idx)} className="text-gray-300 hover:text-red-500">
                        <ICONS.Close className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {p.method === "voucher" && (
                    <div className="ps-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={p.voucher_code}
                          onChange={(e) => updatePayment(idx, { voucher_code: e.target.value, voucher_balance: null, voucher_error: "" })}
                          placeholder={tCheckout("voucher_code_placeholder", "Voucher code")}
                          className="flex-1 text-xs px-2 py-2 rounded-lg border border-gray-100 bg-gray-50/50 focus:outline-none focus:border-[var(--primary-color)]"
                        />
                        <button
                          type="button"
                          onClick={() => checkVoucherCode(idx)}
                          disabled={!p.voucher_code || checkingVoucherIdx === idx}
                          className="shrink-0 text-[11px] font-bold px-2.5 py-2 rounded-lg border border-gray-100 text-gray-500 hover:border-[var(--primary-color)] hover:text-[var(--primary-color)] disabled:opacity-40"
                        >
                          {checkingVoucherIdx === idx ? "…" : tCheckout("btn_check", "Check")}
                        </button>
                      </div>
                      {p.voucher_balance != null && (
                        <p className="text-[11px] text-emerald-600 font-bold mt-1">
                          {tCheckout("label_voucher_balance", "Balance: {amount}").replace("{amount}", `${money(p.voucher_balance)} ${p.voucher_currency_id !== effectiveCurrency?.id ? tCheckout("label_diff_currency", "(different currency — cannot be used here)") : currencyLabel}`)}
                        </p>
                      )}
                      {p.voucher_error && <p className="text-[11px] text-red-500 mt-1">{p.voucher_error}</p>}
                    </div>
                  )}
                </div>
              ))}
              {Math.abs(remaining) > 0.01 && (
                <p className={`text-[11px] font-bold ${remaining > 0 ? "text-amber-500" : "text-red-500"}`}>
                  {remaining > 0 ? `${money(remaining)} remaining` : `${money(-remaining)} over the total`}
                </p>
              )}

              {/* Quick-cash pad — one horizontally-scrolling strip (not a
                  wrapped grid) to save vertical space, shown once for
                  whichever payment row is cash (there's normally just one),
                  not repeated per row. Tapping a note/coin grows that row's
                  received amount, and — until the cashier types a different
                  amount by hand — its applied amount too (see
                  applyDenominationTap). Manage the list per currency from
                  Currencies → CurrencyDenominations.js. The actual
                  "Received" input this feeds sits down by Hold/Complete
                  Sale, not here. */}
              {cashRowIdx !== -1 && (effectiveCurrency?.denominations || []).length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5 pt-1">
                  {effectiveCurrency.denominations.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => applyDenominationTap(cashRowIdx, Number(d.value))}
                      className="shrink-0 whitespace-nowrap px-2.5 py-1 rounded-lg text-[10px] font-bold bg-gray-50 text-gray-600 border border-gray-100 hover:border-[var(--primary-color)] hover:text-[var(--primary-color)] transition-colors"
                    >
                      {currencyLabel} {Number(d.value).toLocaleString()}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleHoldSale}
                loading={holding}
                disabled={submitting}
                className="shrink-0"
                icon={ICONS.Shifts}
              >
                {t("btn_hold", "Hold")}
              </Button>

              {/* The actual "Received" input — sits between Hold and
                  Complete Sale, right where the cashier's eye lands before
                  tapping Complete Sale, instead of buried inside the
                  payment row above. Feeds the same row the quick-cash pad
                  above operates on (cashRowIdx). */}
              {cashRowIdx !== -1 && (
                <div className="flex flex-col items-center gap-0.5 shrink-0">
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={payments[cashRowIdx].received_amount}
                      onChange={(e) => updatePayment(cashRowIdx, { received_amount: e.target.value })}
                      placeholder={tCheckout("received_placeholder", "Received")}
                      className="w-20 text-xs px-2 py-1.5 rounded-lg border border-gray-100 bg-gray-50/50 text-center focus:outline-none focus:border-[var(--primary-color)]"
                    />
                    {payments[cashRowIdx].received_amount !== "" && (
                      <button
                        type="button"
                        onClick={() => updatePayment(cashRowIdx, { received_amount: "" })}
                        className="text-gray-300 hover:text-red-500 shrink-0"
                        title={tCheckout("title_clear_received", "Clear received amount")}
                      >
                        <ICONS.Close className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {changeDue > 0.001 && (
                    <span className="text-[10px] font-mono font-bold text-emerald-600">{tCheckout("label_change_due", "Change {amount}").replace("{amount}", money(changeDue))}</span>
                  )}
                </div>
              )}

              <Button
                variant="primary"
                onClick={handleCompleteSale}
                loading={submitting}
                disabled={holding}
                className="flex-1 shadow-lg shadow-blue-500/10"
                icon={ICONS.Checkout}
              >
                {t("btn_complete_sale", "Complete Sale")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* The gateway-payment waiting screen — shown from the moment
          createOrder comes back status:"awaiting_payment_confirmation"
          (see submitCheckout) until the poll above sees "completed" or
          "cancelled", or the cashier explicitly cancels. Blocks the rest
          of the screen the same way voucherForfeitPrompt below does, so
          the cart can't be edited mid-payment (a gateway-pending order is
          locked server-side too — see CreateOrderHandler/routes.go). */}
      {pendingPayment && (() => {
        const tx = pendingPayment.transaction;
        const isFailedOrExpired = tx?.status === "failed" || tx?.status === "expired";
        const statusLabel = {
          pending: tCheckout("payment_status_pending", "Starting payment…"),
          awaiting_customer: tCheckout("payment_status_awaiting_customer", "Waiting for the customer to pay…"),
          failed: tCheckout("payment_status_failed", "Payment declined"),
          expired: tCheckout("payment_status_expired", "Payment session expired"),
        }[tx?.status] || tCheckout("payment_status_awaiting_customer", "Waiting for the customer to pay…");
        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <div className="bg-white rounded-[2rem] p-6 max-w-sm w-full shadow-2xl text-center">
              {!isFailedOrExpired && (
                <div className="w-10 h-10 mx-auto mb-3 rounded-full border-4 border-gray-100 border-t-[var(--primary-color)] animate-spin" />
              )}
              <h3 className="text-lg font-bold text-gray-800 mb-1">
                {tCheckout("awaiting_payment_title", "Waiting for payment confirmation")}
              </h3>
              <p className="text-sm text-gray-500 mb-1">
                {tCheckout("awaiting_payment_order_label", "Order #{id}").replace("{id}", pendingPayment.order.id)}
              </p>
              <p className="font-mono font-black text-2xl text-gray-900 mb-2">
                {money(pendingPayment.order.grand_total)} {currencyLabel}
              </p>
              <p className={`text-sm font-bold mb-4 ${isFailedOrExpired ? "text-red-500" : "text-amber-600"}`}>
                {statusLabel}
              </p>
              {isFailedOrExpired && tx?.failure_reason && (
                <p className="text-xs text-gray-400 mb-4">{tx.failure_reason}</p>
              )}

              {tx?.hosted_payment_url && !isFailedOrExpired && (
                <button
                  type="button"
                  onClick={reopenHostedPaymentPage}
                  className="text-xs font-bold text-[var(--primary-color)] hover:underline mb-4"
                >
                  {tCheckout("btn_reopen_payment_page", "Reopen payment page")}
                </button>
              )}

              <div className="flex gap-3">
                {isFailedOrExpired && (
                  <Button variant="primary" onClick={handleRetryPayment} loading={payingAction} className="flex-1">
                    {tCheckout("btn_retry_payment", "Retry")}
                  </Button>
                )}
                <Button variant={isFailedOrExpired ? "secondary" : "outline"} onClick={handleCancelPendingPayment} loading={payingAction} className="flex-1">
                  {t("btn_cancel", "Cancel")}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* The confirmed "voucher must be redeemed in full" choice — shown
          only when the backend actually rejects the sale for that reason
          (see submitCheckout). Forfeiting resubmits the exact same sale
          with that voucher's remainder given up; the other option just
          closes this so the cashier can add more items and try again. */}
      {voucherForfeitPrompt && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[2rem] p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-bold text-gray-800 mb-2">{tCheckout("voucher_full_redemption_title", "This voucher must be used in full")}</h3>
            <p className="text-sm text-gray-500 mb-4">
              {tCheckout(
                "voucher_full_redemption_desc",
                "This sale only uses {used} of the voucher's {balance} balance, leaving {shortfall} unused. Add more items to use the full balance, or forfeit the remaining {shortfall} and complete this sale as-is."
              )
                .replace("{used}", money(voucherForfeitPrompt.balance - voucherForfeitPrompt.shortfall))
                .replace("{balance}", money(voucherForfeitPrompt.balance))
                .replaceAll("{shortfall}", money(voucherForfeitPrompt.shortfall))}
            </p>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setVoucherForfeitPrompt(null)} className="flex-1">
                {t("btn_add_more_items", "Add More Items")}
              </Button>
              <Button variant="primary" onClick={confirmForfeitRemainder} loading={submitting} className="flex-1">
                {t("btn_forfeit_complete_sale", "Forfeit & Complete Sale")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
